import React, { useMemo, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
  Switch,
  LinearProgress,
  useTheme,
  alpha,
  Popover,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Watch as WatchIcon,
  DirectionsCar as VehicleIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Palette as PaletteIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { resolvePathfinderColor } from '../../utils/pathfinderColors';

export interface DeviceInfo {
  id: string;
  type: 'watch' | 'vehicle' | 'phone';
  name: string;
  active: boolean;
  color: string;
  lastSeen?: Date;
  batteryLevel?: number;
  dataPointCount?: number;
  totalDataPoints?: number;
}

export interface StudentDevices {
  id: string;
  name: string;
  devices: DeviceInfo[];
  expanded?: boolean;
  selected?: boolean;
}

export interface CoTravelerResult {
  primaryDevice: string;
  coTravelerDevice: string;
  coTravelerEntityType: 'Student' | 'SRP' | 'RP' | 'Unknown' | 'WIFI' | 'BLUETOOTH' | 'CELLULAR';
  encounters: any[];
  totalEncounters: number;
  totalDuration: number;
  averageProximity: number;
  confidenceScore: number;
  temporalOverlap: number;
  spatialOverlap: number;
  frequentLocations: Array<{
    location: [number, number];
    encounters: number;
    totalDuration: number;
  }>;
}

interface HierarchicalDeviceTreeProps {
  students: StudentDevices[];
  onStudentToggle: (studentId: string, selected: boolean) => void;
  onDeviceToggle: (studentId: string, deviceId: string, selected: boolean) => void;
  onStudentExpand: (studentId: string, expanded: boolean) => void;
  selectedStudents: string[];
  selectedDevices: Record<string, string[]>; // studentId -> deviceIds[]
  coTravelerResults?: CoTravelerResult[];
  coTravelerVisibility?: Record<string, boolean>;
  onCoTravelerVisibilityChange?: (visibility: Record<string, boolean>) => void;
  customDeviceColors?: Record<string, string>; // deviceId -> custom color
  onDeviceColorChange?: (deviceId: string, color: string) => void;
}

// Swatches for route / SRP / RP colors. Includes several blue shades so
// nearby SRPs stay distinct, plus other hues that read on the map.
const COLOR_PALETTE = [
  '#B71C1C', '#E53935', '#EF5350', '#FF8A80',
  '#E65100', '#FB8C00', '#FFB300', '#FDD835',
  '#F9A825', '#C0CA33', '#7CB342', '#43A047',
  '#2E7D32', '#00897B', '#00ACC1', '#26C6DA',
  '#01579B', '#0277BD', '#0288D1', '#039BE5',
  '#03A9F4', '#29B6F6', '#4FC3F7', '#81D4FA',
  '#1565C0', '#1E88E5', '#42A5F5', '#90CAF9',
  '#283593', '#3949AB', '#5C6BC0', '#7986CB',
  '#6A1B9A', '#8E24AA', '#AB47BC', '#CE93D8',
  '#AD1457', '#D81B60', '#EC407A', '#F48FB1',
  '#4E342E', '#6D4C41', '#455A64', '#607D8B',
  '#37474F', '#263238', '#FFFFFF', '#000000',
];

const HierarchicalDeviceTree: React.FC<HierarchicalDeviceTreeProps> = ({
  students,
  onStudentToggle: _onStudentToggle,
  onDeviceToggle,
  onStudentExpand,
  selectedStudents: _selectedStudents,
  selectedDevices: _selectedDevices,
  coTravelerResults: _coTravelerResults = [] as CoTravelerResult[],
  coTravelerVisibility: _coTravelerVisibility = {},
  onCoTravelerVisibilityChange: _onCoTravelerVisibilityChange,
  customDeviceColors = {},
  onDeviceColorChange,
}) => {
  const theme = useTheme();
  const { plural: participantPlural } = useParticipantLabels();

  // Color picker popover target. The picker drives BOTH single-device and
  // group ("set color on all WiFi co-travelers") changes - we just widen
  // the state from a single deviceId to {ids[], label, mode} so the same
  // Popover renders both flows. mode='single' is the per-row paint button,
  // mode='group' is the section-header bulk paint button.
  type ColorPickerTarget = {
    ids: string[];
    label: string;
    mode: 'single' | 'group';
  };
  const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);
  const [colorPickerTarget, setColorPickerTarget] = useState<ColorPickerTarget | null>(null);

  // Per-section collapse state for co-traveler type groups.
  // Sections with >5 results start collapsed by default so 200 WiFi candidates
  // don't flood the side panel.
  const [collapsedCoTravTypes, setCollapsedCoTravTypes] = useState<Record<string, boolean>>({});

  const handleColorPickerOpen = (event: React.MouseEvent<HTMLElement>, deviceId: string) => {
    event.stopPropagation();
    setColorPickerAnchor(event.currentTarget);
    setColorPickerTarget({ ids: [deviceId], label: deviceId, mode: 'single' });
  };

  const handleColorPickerOpenGroup = (
    event: React.MouseEvent<HTMLElement>,
    deviceIds: string[],
    label: string,
  ) => {
    event.stopPropagation();
    if (deviceIds.length === 0) return;
    setColorPickerAnchor(event.currentTarget);
    setColorPickerTarget({ ids: deviceIds, label, mode: 'group' });
  };

  const handleColorPickerClose = () => {
    setColorPickerAnchor(null);
    setColorPickerTarget(null);
  };

  const applyColor = (color: string, close: boolean) => {
    if (colorPickerTarget && onDeviceColorChange) {
      colorPickerTarget.ids.forEach(id => onDeviceColorChange(id, color));
    }
    if (close) handleColorPickerClose();
  };

  const handleColorSelect = (color: string) => {
    applyColor(color, true);
  };

  const handleColorReset = () => {
    if (colorPickerTarget && onDeviceColorChange) {
      // Empty string is the existing "reset to default" sentinel used by the
      // primary-device color picker, preserved for parity.
      colorPickerTarget.ids.forEach(id => onDeviceColorChange(id, ''));
    }
    handleColorPickerClose();
  };

  // True when the popover's current target already has a custom color set.
  // For groups we treat it as "any of the targeted ids has a custom color"
  // so the user can wipe a partially-applied group palette in one click.
  const colorPickerHasCustom = !!(
    colorPickerTarget &&
    colorPickerTarget.ids.some(id => !!customDeviceColors[id])
  );

  // For the "currently-selected" swatch outline in the picker. In group mode
  // we only outline a swatch if EVERY targeted id is on that color
  // (otherwise the group has a mixed state and no swatch should look active).
  const colorPickerActiveColor: string | null = (() => {
    if (!colorPickerTarget) return null;
    const colors = colorPickerTarget.ids
      .map(id => customDeviceColors[id])
      .filter(Boolean);
    if (colors.length !== colorPickerTarget.ids.length) return null;
    const first = colors[0];
    return colors.every(c => c === first) ? first : null;
  })();
  
  // Get the display color for a device (custom → baked-in device.color → type default).
  // AdTech PoL devices get a per-registration color stored on device.color at extract time.
  const getDeviceDisplayColor = (device: DeviceInfo): string => {
    if (customDeviceColors[device.id]) {
      return customDeviceColors[device.id];
    }
    if (device.color) {
      return device.color;
    }
    return getDeviceTypeColor(device.type);
  };
  
  const getDeviceIcon = (type: string, color?: string) => {
    const iconColor = color || getDeviceTypeColor(type);
    switch (type) {
      case 'watch':
        return <WatchIcon sx={{ fontSize: 16, color: iconColor }} />;
      case 'vehicle':
        return <VehicleIcon sx={{ fontSize: 16, color: iconColor }} />;
      case 'phone':
        return <PhoneIcon sx={{ fontSize: 16, color: iconColor }} />;
      default:
        return <PersonIcon sx={{ fontSize: 16, color: iconColor }} />;
    }
  };

  // Thin wrapper around the shared color resolver so the tree paints the
  // same hue the map / route layer paints for the same device class.
  const getDeviceTypeColor = (type: string): string => {
    return resolvePathfinderColor({ deviceType: type });
  };

  const getDeviceStatusColor = (device: DeviceInfo): string => {
    if (!device.active) return '#f44336'; // Red for inactive
    if (device.batteryLevel && device.batteryLevel < 20) return '#ff9800'; // Orange for low battery
    return '#4caf50'; // Green for active
  };

  const formatDataPercentage = (device: DeviceInfo): string => {
    if (!device.dataPointCount || !device.totalDataPoints || device.totalDataPoints === 0) {
      return '0%';
    }
    const percentage = (device.dataPointCount / device.totalDataPoints) * 100;
    return `${percentage.toFixed(1)}%`;
  };

  const formatDataStats = (device: DeviceInfo): string => {
    if (!device.dataPointCount) return '0 points';
    return `${device.dataPointCount.toLocaleString()} pts`;
  };

  // Co-traveler helper functions
  // const getEntityIcon = (entityType: string) => {
  //   switch (entityType) {
  //     case 'Student':
  //       return <StudentIcon sx={{ fontSize: 14, color: '#2196f3' }} />;
  //     case 'SRP':
  //       return <SRPIcon sx={{ fontSize: 14, color: '#f44336' }} />;
  //     case 'RP':
  //       return <RPIcon sx={{ fontSize: 14, color: '#ff9800' }} />;
  //     default:
  //       return <UnknownIcon sx={{ fontSize: 14, color: '#757575' }} />;
  //   }
  // };

  // Thin wrapper around the shared resolver. The previous body duplicated
  // the entity_type / device_type palette inline; centralizing it kills the
  // long-running drift where the cotraveler list and the map could disagree
  // on what color a given (entity_type, device_type) pair should be.
  // The deviceId argument is optional but preferred - passing it lets the
  // resolver honor any per-device custom color the user has set via the
  // co-traveler row paint button.
  const getEntityColor = (
    entityType: string,
    deviceType?: string,
    deviceId?: string,
  ): string => {
    return resolvePathfinderColor({ entityType, deviceType, deviceId, customDeviceColors });
  };

  const maxEncounters = useMemo(() => {
    return _coTravelerResults.reduce((max, r) => Math.max(max, r.totalEncounters), 0) || 1;
  }, [_coTravelerResults]);

  // Per signal-type maxes so digital co-traveler progress bars fill relative to
  // their own group. E.g. a WiFi SSID with 2371 sightings should render full,
  // while a BT device with 138 sightings should render relative to the top BT
  // result (not relative to the global WiFi max which would crush it to ~5%).
  const maxEncountersByType = useMemo(() => {
    const acc: Record<string, number> = {};
    _coTravelerResults.forEach((r: any) => {
      const t = r.coTravelerEntityType || 'Unknown';
      acc[t] = Math.max(acc[t] || 0, r.totalEncounters || 0);
    });
    return acc;
  }, [_coTravelerResults]);

  // Progress-bar fill percentage for a co-traveler row.
  //
  // Digital co-travelers (WiFi / Bluetooth / Cellular) use an encounters-based
  // ratio scaled to the per-type max because the user's mental model for RF
  // emitters is "how many sightings" (AirPods seen 2371x should read as a full
  // bar), not a 0..1 confidence. The backend now returns a properly bounded
  // `confidence` field too (saturating curve over co_occurrence_score) which
  // is plumbed onto `confidenceScore` for popup display, but we still prefer
  // the encounters-based bar for the dominant visual.
  //
  // Physical co-travelers (Student/SRP/RP) continue to use confidenceScore
  // (proximity-derived, 0-1) directly.
  const getPercent = useCallback((result: CoTravelerResult): number => {
    const entityType = result.coTravelerEntityType || '';
    const isDigital = entityType === 'WIFI' || entityType === 'BLUETOOTH' || entityType === 'CELLULAR';
    if (isDigital) {
      const typeMax = maxEncountersByType[entityType] || maxEncounters || 1;
      return Math.max(1, Math.round((result.totalEncounters / typeMax) * 100));
    }
    if (result.confidenceScore && result.confidenceScore > 0) {
      return Math.round(result.confidenceScore * 100);
    }
    return Math.round((result.totalEncounters / maxEncounters) * 100);
  }, [maxEncounters, maxEncountersByType]);

  // Memoize one global co-traveler group. Co-travelers represent relationships
  // to the selected entities, not child devices, so keep them out of device rows.
  const groupedCoTravelers = useMemo(() => {
    const grouped = _coTravelerResults.reduce((acc, result) => {
      const entityType = result.coTravelerEntityType;
      if (!acc[entityType]) {
        acc[entityType] = [];
      }
      acc[entityType].push(result);
      return acc;
    }, {} as Record<string, CoTravelerResult[]>);

    // Sort each group by percentage (descending). Use getPercent so the visual
    // order matches the bar fill — digital rows sort by sighting count relative
    // to per-type max, physical rows by confidenceScore.
    Object.keys(grouped).forEach(entityType => {
      grouped[entityType].sort((a, b) => getPercent(b) - getPercent(a));
    });

    return grouped;
  }, [_coTravelerResults, getPercent]);

  const handleCoTravelerToggle = (resultKey: string, visible: boolean) => {
    if (_onCoTravelerVisibilityChange) {
      _onCoTravelerVisibilityChange({
        ..._coTravelerVisibility,
        [resultKey]: visible
      });
    }
  };

  // Toggle all co-travelers in a section on or off
  const handleToggleAllCoTravelers = (deviceCoTravelers: CoTravelerResult[], turnOn: boolean) => {
    if (!_onCoTravelerVisibilityChange) return;
    
    const newVisibility = { ..._coTravelerVisibility };
    deviceCoTravelers.forEach(result => {
      const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
      newVisibility[resultKey] = turnOn;
    });
    _onCoTravelerVisibilityChange(newVisibility);
  };

  // Check if all co-travelers for a device are visible
  const areAllCoTravelersVisible = (deviceCoTravelers: CoTravelerResult[]): boolean => {
    if (deviceCoTravelers.length === 0) return false;
    return deviceCoTravelers.every(result => {
      const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
      return _coTravelerVisibility[resultKey] === true;
    });
  };

  // Check if any co-travelers for a device are visible
  const areAnyCoTravelersVisible = (deviceCoTravelers: CoTravelerResult[]): boolean => {
    return deviceCoTravelers.some(result => {
      const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
      return _coTravelerVisibility[resultKey] === true;
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {students.map((student, index) => {
        const studentDevices = _selectedDevices[student.id] || [];
        const activeDevices = student.devices.filter(d => d.active).length;
        const totalDevices = student.devices.length;
        const availableDeviceIds = student.devices.filter(d => d.active).map(d => d.id);
        // Student checkbox reflects device selection: checked if all active devices selected, indeterminate if some
        const allDevicesSelected = availableDeviceIds.length > 0 && availableDeviceIds.every(id => studentDevices.includes(id));
        const someDevicesSelected = studentDevices.length > 0 && !allDevicesSelected;
        
        // Auto-collapse all except the first student
        const isExpanded = student.expanded !== undefined ? student.expanded : (index === 0);

        return (
          <Accordion
            key={student.id}
            expanded={isExpanded}
            onChange={(_, expanded) => onStudentExpand(student.id, expanded)}
            sx={{
              boxShadow: 'none',
              border: 'none',
              bgcolor: 'background.paper',
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                minHeight: 'auto',
                padding: '2px 6px',
                backgroundColor: (allDevicesSelected || someDevicesSelected) ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                borderLeft: (allDevicesSelected || someDevicesSelected) ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              },
              '& .MuiAccordionDetails-root': {
                padding: 0,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.2rem' }} />}
              sx={{ 
                '& .MuiAccordionSummary-content': { 
                  margin: '4px 0 !important' // Reduce vertical margin
                } 
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                    {student.name}
                  </Typography>
                  <Chip
                    label={`${activeDevices}/${totalDevices}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 14,
                      fontSize: '0.6rem',
                      color: activeDevices > 0 ? 'success.main' : 'text.secondary',
                      borderColor: activeDevices > 0 ? 'success.main' : 'text.secondary',
                      '& .MuiChip-label': { padding: '0 4px' }
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 0.25, mr: 0.5 }}>
                  <Tooltip title="Show all devices">
                    <IconButton
                      size="small"
                      disabled={availableDeviceIds.length === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        _onStudentToggle(student.id, true);
                      }}
                      onFocus={(e) => e.stopPropagation()}
                      sx={{
                        p: 0.25,
                        color: allDevicesSelected ? 'success.main' : 'text.disabled',
                        '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.1) },
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Hide all devices">
                    <IconButton
                      size="small"
                      disabled={availableDeviceIds.length === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        _onStudentToggle(student.id, false);
                      }}
                      onFocus={(e) => e.stopPropagation()}
                      sx={{
                        p: 0.25,
                        color: !someDevicesSelected && !allDevicesSelected ? 'error.main' : 'text.disabled',
                        '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) },
                      }}
                    >
                      <VisibilityOffIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails>
              <Box sx={{ pl: 0.5 }}>
                {student.devices.map((device) => {
                  const isDeviceSelected = studentDevices.includes(device.id);
                  const statusColor = getDeviceStatusColor(device);
                  const displayColor = getDeviceDisplayColor(device);
                  const hasCustomColor = !!customDeviceColors[device.id];

                  return (
                    <Box
                      key={device.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 0.2,
                        px: 0.3,
                        borderRadius: 0.5,
                        backgroundColor: isDeviceSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                        borderLeft: isDeviceSelected ? `2px solid ${displayColor}` : '2px solid transparent',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Checkbox
                        checked={isDeviceSelected}
                        onChange={(e) => onDeviceToggle(student.id, device.id, e.target.checked)}
                        disabled={!device.active}
                        sx={{ 
                          padding: '1px',
                          color: displayColor,
                          '& .MuiSvgIcon-root': { fontSize: '1rem' },
                          '&.Mui-checked': { color: displayColor },
                        }}
                      />
                      
                      {/* Color picker for this device */}
                      <Tooltip title="Change route color">
                        <IconButton
                          size="small"
                          onClick={(e) => handleColorPickerOpen(e, device.id)}
                          sx={{ 
                            p: 0.2,
                            mr: 0.3,
                            '&:hover': { backgroundColor: alpha(displayColor, 0.2) }
                          }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '2px',
                              backgroundColor: displayColor,
                              border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {hasCustomColor && (
                              <PaletteIcon sx={{ fontSize: 7, color: 'white', opacity: 0.9 }} />
                            )}
                          </Box>
                        </IconButton>
                      </Tooltip>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 0.3 }}>
                        <Box sx={{ '& .MuiSvgIcon-root': { fontSize: '0.9rem' } }}>
                          {getDeviceIcon(device.type, displayColor)}
                        </Box>
                        
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {device.name || `${device.type.charAt(0).toUpperCase() + device.type.slice(1)}`}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.2, flexShrink: 0, mr: 0.5 }}>
                          <Box
                            sx={{
                              width: 5,
                              height: 5,
                              borderRadius: '50%',
                              backgroundColor: statusColor,
                            }}
                          />
                          
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '0.65rem', 
                              fontWeight: 'bold',
                              color: displayColor,
                              minWidth: '40px',
                              textAlign: 'right',
                              lineHeight: 1.1,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {formatDataPercentage(device)}
                          </Typography>
                          
                          <Tooltip title={`Data points: ${device.dataPointCount?.toLocaleString() || 0} out of ${device.totalDataPoints?.toLocaleString() || 0} total`}>
                            <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary', lineHeight: 1, whiteSpace: 'nowrap' }}>
                              {formatDataStats(device)}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                {student.devices.length === 0 && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      No devices registered
                    </Typography>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {_coTravelerResults.length > 0 && (() => {
        const allVisible = areAllCoTravelersVisible(_coTravelerResults);
        const anyVisible = areAnyCoTravelersVisible(_coTravelerResults);

        return (
          <Box
            sx={{
              mt: 1,
              px: 1,
              py: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.action.hover,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.75rem' }}>
                Co-Travelers ({_coTravelerResults.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Show all co-travelers">
                  <IconButton
                    size="small"
                    onClick={() => handleToggleAllCoTravelers(_coTravelerResults, true)}
                    sx={{
                      p: 0.25,
                      color: allVisible ? 'success.main' : 'text.disabled',
                      '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.1) }
                    }}
                  >
                    <VisibilityIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Hide all co-travelers">
                  <IconButton
                    size="small"
                    onClick={() => handleToggleAllCoTravelers(_coTravelerResults, false)}
                    sx={{
                      p: 0.25,
                      color: !anyVisible ? 'error.main' : 'text.disabled',
                      '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                    }}
                  >
                    <VisibilityOffIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Unified bottom co-traveler sections for physical and DFP/RF candidates. */}
            {([
              { key: 'SRP' as const, label: 'SRP' },
              { key: 'RP' as const, label: 'RP' },
              { key: 'WIFI' as const, label: 'Wi-Fi' },
              { key: 'BLUETOOTH' as const, label: 'Bluetooth' },
              { key: 'CELLULAR' as const, label: 'Cellular' },
              { key: 'Student' as const, label: participantPlural },
            ]).map(({ key: typeKey, label }) => {
              const typeResults = groupedCoTravelers[typeKey] || [];
              if (typeResults.length === 0) return null;

              const sectionKey = `global-${typeKey}`;
              const isCollapsed = collapsedCoTravTypes[sectionKey] ?? (typeResults.length > 5);
              const visibleCount = typeResults.filter(r => {
                const rk = `${r.primaryDevice}-${r.coTravelerDevice}`;
                return _coTravelerVisibility[rk] === true;
              }).length;

              const sectionDeviceIds = typeResults.map(r => r.coTravelerDevice);
              const sectionHasCustom = sectionDeviceIds.some(id => !!customDeviceColors[id]);

              return (
                <Box key={typeKey} sx={{ mt: 0.5 }}>
                  <Box
                    onClick={() => setCollapsedCoTravTypes(prev => ({ ...prev, [sectionKey]: !isCollapsed }))}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      py: 0.25,
                      borderRadius: 0.5,
                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
                    }}
                  >
                    <ExpandMoreIcon sx={{
                      fontSize: 14,
                      color: 'text.secondary',
                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      mr: 0.25,
                    }} />
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: '600', color: 'text.secondary', flex: 1 }}>
                      {label}
                    </Typography>
                    <Chip
                      label={visibleCount > 0 ? `${visibleCount}/${typeResults.length}` : `${typeResults.length}`}
                      size="small"
                      sx={{
                        height: 14,
                        fontSize: '0.55rem',
                        color: visibleCount > 0 ? 'success.main' : 'text.disabled',
                        borderColor: visibleCount > 0 ? 'success.main' : 'text.disabled',
                        '& .MuiChip-label': { px: '4px' },
                        mr: 0.5,
                      }}
                      variant="outlined"
                    />
                    {/* Group paint - sets one color across every cotraveler in
                        this section. Hidden when the parent didn't wire up
                        onDeviceColorChange (e.g. tests / read-only contexts). */}
                    {onDeviceColorChange && sectionDeviceIds.length > 0 && (
                      <Tooltip title={`Color all ${label} co-travelers`}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleColorPickerOpenGroup(e, sectionDeviceIds, label)}
                          sx={{
                            p: 0.25,
                            color: sectionHasCustom ? 'primary.main' : 'text.disabled',
                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
                          }}
                        >
                          <PaletteIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {!isCollapsed && typeResults.map((result) => {
                    const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
                    const isVisible = _coTravelerVisibility[resultKey] ?? false;
                    const percent = getPercent(result);
                    const deviceType = typeKey === 'Student'
                      ? (result.coTravelerDevice.includes('-phone') ? 'phone' :
                         result.coTravelerDevice.includes('-vehicle') ? 'vehicle' :
                         result.coTravelerDevice.includes('-watch') ? 'watch' : 'phone')
                      : undefined;
                    // Pass the cotraveler's deviceId so any per-device custom
                    // color the user picked applies in the tree as well as on
                    // the map.
                    const entityColor = getEntityColor(
                      result.coTravelerEntityType,
                      deviceType,
                      result.coTravelerDevice,
                    );
                    const rowHasCustom = !!customDeviceColors[result.coTravelerDevice];

                    return (
                      <Box key={resultKey} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1, py: 0.25, pr: 0.5 }}>
                        <Switch
                          size="small"
                          checked={isVisible}
                          onChange={(e) => handleCoTravelerToggle(resultKey, e.target.checked)}
                          sx={{
                            '& .MuiSwitch-thumb': { width: 12, height: 12 },
                            '& .MuiSwitch-track': { backgroundColor: entityColor + '40' },
                            '&.Mui-checked .MuiSwitch-thumb': { backgroundColor: entityColor },
                            flexShrink: 0
                          }}
                        />
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', minWidth: 50, maxWidth: 90, color: entityColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {result.coTravelerDevice}
                        </Typography>
                        <Box sx={{ flex: 1, minWidth: 30, maxWidth: 80 }}>
                          <LinearProgress
                            variant="determinate"
                            value={percent}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              '& .MuiLinearProgress-bar': { backgroundColor: entityColor }
                            }}
                          />
                        </Box>
                        <Tooltip title={`${result.totalEncounters} encounters`}>
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: 35, textAlign: 'right', color: entityColor, flexShrink: 0 }}>
                            {result.totalEncounters}x
                          </Typography>
                        </Tooltip>
                        {/* Per-row paint button - same affordance as the
                            primary-device color picker. Only renders when the
                            parent wired up onDeviceColorChange. */}
                        {onDeviceColorChange && (
                          <Tooltip title={rowHasCustom ? 'Change color' : 'Pick color'}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleColorPickerOpen(e, result.coTravelerDevice)}
                              sx={{
                                p: 0.25,
                                color: rowHasCustom ? entityColor : 'text.disabled',
                                '&:hover': { backgroundColor: alpha(entityColor, 0.1) },
                              }}
                            >
                              <PaletteIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        );
      })()}
      
      {students.length === 0 && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No students available. Select a course to load students.
          </Typography>
        </Box>
      )}
      
      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={handleColorPickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 1.5, width: 248 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.25, display: 'block' }}>
            {colorPickerTarget?.mode === 'group'
              ? `Color all ${colorPickerTarget.label} (${colorPickerTarget.ids.length})`
              : 'Select Route Color'}
          </Typography>
          {colorPickerTarget?.mode === 'single' && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.65rem',
                mb: 0.75,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={colorPickerTarget.label}
            >
              {colorPickerTarget.label}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {COLOR_PALETTE.map((color) => {
              const isActive = (colorPickerActiveColor || '').toLowerCase() === color.toLowerCase();
              return (
                <Box
                  key={color}
                  onClick={() => handleColorSelect(color)}
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: color,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    border: isActive ? '2px solid white' : '1px solid rgba(255,255,255,0.2)',
                    boxShadow: isActive ? '0 0 0 1px rgba(0,0,0,0.5)' : 'none',
                    '&:hover': {
                      transform: 'scale(1.15)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    },
                    transition: 'transform 0.1s, box-shadow 0.1s',
                  }}
                />
              );
            })}
          </Box>
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
              Custom
            </Typography>
            <Box
              component="input"
              type="color"
              aria-label="Pick a custom color"
              value={/^#[0-9A-Fa-f]{6}$/.test(colorPickerActiveColor || '') ? colorPickerActiveColor! : '#1976D2'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => applyColor(e.target.value, false)}
              sx={{
                width: 36,
                height: 28,
                p: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '4px',
                bgcolor: 'transparent',
                cursor: 'pointer',
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
              {(/^#[0-9A-Fa-f]{6}$/.test(colorPickerActiveColor || '') ? colorPickerActiveColor : '#1976D2')?.toUpperCase()}
            </Typography>
          </Box>
          {colorPickerHasCustom && (
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={handleColorReset}
              >
                {colorPickerTarget?.mode === 'group' ? 'Reset Group to Default' : 'Reset to Default'}
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default HierarchicalDeviceTree;
